import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatMoney } from '../ui/format';
import { Badge, Panel, type BadgeTone } from '../ui/Page';
import { getTopUp, listTopUps, simulateTopUp, startTopUp, type TopUp } from './api';
import { useRealtimeTopic } from '../app/realtime-store';

/**
 * Adding credit (context.md §71, §73).
 *
 * An amount from the presets or typed, Pix or card, and the provider's answer on the same screen:
 * the Pix code to copy and its QR, or a button to the card checkout. The panel polls the top-up
 * until the money arrives and the balance above it moves. A campaign cannot be scheduled without
 * balance, so this is where somebody goes when the estimate says "not enough".
 */
export function TopUpPanel(): React.JSX.Element {
  const client = useQueryClient();
  const [amount, setAmount] = useState<number>(5_000);
  const [custom, setCustom] = useState('');
  const [method, setMethod] = useState<'PIX' | 'CARD'>('PIX');
  const [open, setOpen] = useState<TopUp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const history = useQuery({ queryKey: ['sms-topups'], queryFn: listTopUps });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['sms-topups'] });
    void client.invalidateQueries({ queryKey: ['sms-wallet'] });
    void client.invalidateQueries({ queryKey: ['sms-ledger'] });
  };

  /*
   * The provider's webhook says when the money arrived (Phase 80).
   *
   * Somebody is looking at a Pix code with their bank open in the other hand, so this is the screen
   * where a four-second poll felt necessary. It stays as a slow floor: the webhook may be late, and
   * a payment that cleared while the socket was down must still show up.
   */
  const pending = useQuery({
    queryKey: ['sms-topup', open?.id],
    queryFn: () => getTopUp(open!.id),
    enabled: open !== null && open.status === 'PENDING',
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 30_000 : false),
  });

  useRealtimeTopic(open && open.status === 'PENDING' ? 'sms_topup' : null, () => {
    void client.invalidateQueries({ queryKey: ['sms-topup', open?.id] });
    void client.invalidateQueries({ queryKey: ['sms-wallet'] });
    void client.invalidateQueries({ queryKey: ['sms-ledger'] });
  });

  const live = pending.data ?? open;

  if (live && open && live.status !== open.status) {
    setOpen(live);
    refresh();
  }

  const start = useMutation({
    mutationFn: () =>
      startTopUp({
        amountCents: custom ? Math.round(Number(custom.replace(',', '.')) * 100) : amount,
        method,
      }),
    onSuccess: (topup) => {
      setOpen(topup);
      setError(null);
      setCopied(false);
      refresh();
      if (topup.method === 'CARD' && topup.checkoutUrl)
        window.open(topup.checkoutUrl, '_blank', 'noopener');
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const simulate = useMutation({
    mutationFn: (id: string) => simulateTopUp(id),
    onSuccess: (topup) => {
      setOpen(topup);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const presets = history.data?.presets_cents ?? [2_000, 5_000, 10_000, 20_000, 50_000];
  const local = history.data?.provider === 'local';

  return (
    <Panel title={t('sms.topup.title')}>
      {live ? (
        <div className="stack">
          <div className="cell-inline">
            <Badge tone={toneOf(live.status)}>{t(`sms.topup.status.${live.status}`)}</Badge>
            <strong>{formatMoney(live.amountCents, live.currency)}</strong>
            <span className="muted small">{t(`sms.topup.method.${live.method}`)}</span>
          </div>

          {live.status === 'PENDING' && live.method === 'PIX' && live.pixPayload && (
            <div className="pix">
              {live.pixQrBase64 && (
                <img
                  className="pix__qr"
                  src={`data:image/png;base64,${live.pixQrBase64}`}
                  alt={t('sms.topup.qrAlt')}
                />
              )}
              <div className="stack">
                <p className="muted small">{t('sms.topup.pixHint')}</p>
                <code className="token">{live.pixPayload}</code>
                <div className="cell-inline">
                  <button
                    type="button"
                    className="button button--compact"
                    onClick={() =>
                      void navigator.clipboard
                        ?.writeText(live.pixPayload!)
                        .then(() => setCopied(true))
                    }
                  >
                    {copied ? t('integrations.copied') : t('sms.topup.copyPix')}
                  </button>
                  {live.expiresAt && (
                    <span className="muted small">
                      {t('sms.topup.expires')} {formatDateTime(live.expiresAt)}
                    </span>
                  )}
                </div>
                <p className="muted small">{t('sms.topup.waiting')}</p>
              </div>
            </div>
          )}

          {live.status === 'PENDING' && live.method === 'CARD' && live.checkoutUrl && (
            <p>
              <a
                className="button button--primary button--compact"
                href={live.checkoutUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t('sms.topup.openCheckout')}
              </a>
              <span className="muted small"> {t('sms.topup.waiting')}</span>
            </p>
          )}

          {live.status === 'PAID' && <p className="alert alert--positive">{t('sms.topup.paid')}</p>}
          {live.status === 'FAILED' && (
            <p className="alert">{live.failureReason ?? t('sms.topup.failed')}</p>
          )}

          <div className="cell-inline">
            {local && live.status === 'PENDING' && (
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={simulate.isPending}
                onClick={() => simulate.mutate(live.id)}
              >
                {t('sms.topup.simulate')}
              </button>
            )}
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setOpen(null)}
            >
              {live.status === 'PENDING' ? t('sms.topup.later') : t('common.close')}
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="muted small">{t('sms.topup.hint')}</p>

          <div className="chips">
            {presets.map((cents) => (
              <button
                key={cents}
                type="button"
                className={`chip${!custom && amount === cents ? ' chip--active' : ''}`}
                onClick={() => {
                  setAmount(cents);
                  setCustom('');
                }}
              >
                {formatMoney(cents)}
              </button>
            ))}
            <input
              className="topup__custom"
              inputMode="decimal"
              value={custom}
              placeholder={t('sms.topup.other')}
              onChange={(event) => setCustom(event.target.value)}
            />
          </div>

          <div className="chips" role="radiogroup" aria-label={t('sms.topup.how')}>
            {(['PIX', 'CARD'] as const).map((choice) => (
              <button
                key={choice}
                type="button"
                role="radio"
                aria-checked={method === choice}
                className={`chip${method === choice ? ' chip--active' : ''}`}
                onClick={() => setMethod(choice)}
              >
                {t(`sms.topup.method.${choice}`)}
              </button>
            ))}
          </div>

          {error && (
            <p className="alert small" role="alert">
              {error}
            </p>
          )}

          <div>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={start.isPending}
              onClick={() => start.mutate()}
            >
              {t('sms.topup.start')}
            </button>
          </div>

          {(history.data?.topups.length ?? 0) > 0 && (
            <ul className="home-list">
              {history.data!.topups.slice(0, 5).map((topup) => (
                <li key={topup.id}>
                  <span className="cell-inline">
                    <Badge tone={toneOf(topup.status)}>
                      {t(`sms.topup.status.${topup.status}`)}
                    </Badge>
                    <span>{formatMoney(topup.amountCents, topup.currency)}</span>
                    <span className="muted small">
                      {t(`sms.topup.method.${topup.method}`)} · {formatDateTime(topup.createdAt)}
                    </span>
                  </span>
                  {topup.status === 'PENDING' && (
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      onClick={() => setOpen(topup)}
                    >
                      {t('sms.topup.resume')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Panel>
  );
}

function toneOf(status: TopUp['status']): BadgeTone {
  return status === 'PAID'
    ? 'positive'
    : status === 'PENDING'
      ? 'warning'
      : status === 'FAILED'
        ? 'danger'
        : 'neutral';
}
